import React, { useState, useMemo, useEffect } from 'react';
import { usePackagingOperationsContext } from '../context/PackagingOperationsContext';
import { pkgOperationsService } from '../services/pkgOperationsService';
import {
    CREAM_VARIETIES_CATALOG,
    getCreamStandardForVariety
} from '../constants/creamStandards';

interface CreamMachineRecord {
    id: string;
    machineNo: string;
    sandwichWeights: string[];
    shellWeights: string[];
    isSaved?: boolean;
}

const DEFAULT_MACHINE_OPTIONS = [
    'M/C-01', 'M/C-02', 'M/C-03', 'M/C-04', 'M/C-05',
    'M/C-06', 'M/C-07', 'M/C-08', 'M/C-09', 'M/C-10'
];

const CreamPercentage: React.FC = () => {
    const { state, dispatch } = usePackagingOperationsContext();

    // ─── 1. Standards & Variety Setup ───
    const initialLine = state.sessionData.lineNo || 'Line 7';
    const initialVariety = CREAM_VARIETIES_CATALOG.find(v => v.lineName === initialLine) || CREAM_VARIETIES_CATALOG[0];


    const [metadata, setMetadata] = useState({
        productName: initialVariety?.productName || 'BOURBON 100GM [10 KG] [MRP 21/-]',
        productCode: initialVariety?.productCode || '5002171',
        line: initialLine,
        standardCreamPercentage: initialVariety?.standardCreamPercentage.toFixed(2) || '24.00',
        standardMin: initialVariety?.standardMin.toFixed(2) || '22.50',
        standardMax: initialVariety?.standardMax.toFixed(2) || '25.50',
        ruleDescription: initialVariety?.description || 'Bourbon Recipe QA Standard (24.00% ± 1.50%)',
        executiveName: state.sessionData.qaExecutive || ''
    });

    // ─── 2. Machine-Wise Selection State (Supports Up to 10 Machines) ───
    const [machines, setMachines] = useState<CreamMachineRecord[]>([
        {
            id: 'm-1',
            machineNo: 'M/C-01',
            sandwichWeights: ['', '', '', ''],
            shellWeights: ['', '', '', ''],
            isSaved: false
        }
    ]);
    const [activeMachineIndex, setActiveMachineIndex] = useState<number>(0);
    const [customMachineInput, setCustomMachineInput] = useState<string>('');

    // Catalog search query
    const [varietySearch, setVarietySearch] = useState<string>('');

    // Filter varieties by active line and search term
    const availableVarieties = useMemo(() => {
        return CREAM_VARIETIES_CATALOG.filter(v => {
            const matchesSearch = !varietySearch ||
                v.productName.toLowerCase().includes(varietySearch.toLowerCase()) ||
                v.productCode.includes(varietySearch);
            return matchesSearch;
        });
    }, [varietySearch]);

    // When variety changes: auto-populate standards and SKU
    const handleVarietyChange = (selectedName: string) => {
        const found = CREAM_VARIETIES_CATALOG.find(v => v.productName === selectedName);
        const std = getCreamStandardForVariety(selectedName);

        setMetadata(prev => ({
            ...prev,
            productName: selectedName,
            productCode: found?.productCode || '',
            standardCreamPercentage: std.standardCreamPercentage.toFixed(2),
            standardMin: std.standardMin.toFixed(2),
            standardMax: std.standardMax.toFixed(2),
            ruleDescription: std.ruleDescription
        }));
    };

    // Active machine shorthand
    const activeMachine = machines[activeMachineIndex] || machines[0];

    // Handle updating weight for the active machine
    const handleWeightChange = (type: 'sandwich' | 'shell', sampleIndex: number, value: string) => {
        setMachines(prev => prev.map((m, idx) => {
            if (idx !== activeMachineIndex) return m;
            const updatedWeights = type === 'sandwich' ? [...m.sandwichWeights] : [...m.shellWeights];
            updatedWeights[sampleIndex] = value;
            return {
                ...m,
                [type === 'sandwich' ? 'sandwichWeights' : 'shellWeights']: updatedWeights,
                isSaved: false
            };
        }));
    };

    // Change Machine Number for the current machine
    const handleMachineNoChange = (newMachineNo: string) => {
        setMachines(prev => prev.map((m, idx) => {
            if (idx === activeMachineIndex) {
                return { ...m, machineNo: newMachineNo };
            }
            return m;
        }));
    };

    // Add another machine (up to 10)
    const handleAddMachine = () => {
        if (machines.length >= 10) {
            alert('Maximum of 10 machines can be evaluated in one session.');
            return;
        }
        const nextNum = machines.length + 1;
        const defaultCode = `M/C-0${nextNum > 9 ? nextNum : `0${nextNum}`.slice(-2)}`;
        const newMachine: CreamMachineRecord = {
            id: `m-${Date.now()}`,
            machineNo: defaultCode,
            sandwichWeights: ['', '', '', ''],
            shellWeights: ['', '', '', ''],
            isSaved: false
        };
        setMachines(prev => [...prev, newMachine]);
        setActiveMachineIndex(machines.length);
    };

    // Remove a machine
    const handleRemoveMachine = (idxToRemove: number) => {
        if (machines.length <= 1) {
            alert('At least one machine record must be present.');
            return;
        }
        setMachines(prev => prev.filter((_, i) => i !== idxToRemove));
        if (activeMachineIndex >= idxToRemove && activeMachineIndex > 0) {
            setActiveMachineIndex(activeMachineIndex - 1);
        }
    };

    // ─── 3. Automatic Formula: Cream % = ((Sandwich - Shell) / Sandwich) * 100 ───
    const calculateSampleCreamPct = (sandwichStr: string, shellStr: string) => {
        const sandwich = parseFloat(sandwichStr);
        const shell = parseFloat(shellStr);
        if (isNaN(sandwich) || sandwich <= 0) return null;
        if (isNaN(shell) || shell < 0) return null;
        if (shell > sandwich) return { pct: 0, error: 'Shell > Sandwich' };
        const pct = ((sandwich - shell) / sandwich) * 100;
        return { pct, pctStr: pct.toFixed(2) };
    };

    // Machine-wise metrics calculator
    const getMachineMetrics = (m: CreamMachineRecord) => {
        const samples = m.sandwichWeights.map((sw, i) => {
            const shw = m.shellWeights[i];
            const result = calculateSampleCreamPct(sw, shw);
            return {
                index: i + 1,
                sandwich: sw,
                shell: shw,
                result
            };
        });

        const validPcts = samples
            .filter(s => s.result && !s.result.error && s.result.pct > 0)
            .map(s => s.result!.pct);

        const avg = validPcts.length > 0 ? validPcts.reduce((a, b) => a + b, 0) / validPcts.length : 0;
        const avgStr = avg > 0 ? avg.toFixed(2) : '0.00';

        const min = parseFloat(metadata.standardMin) || 0;
        const max = parseFloat(metadata.standardMax) || 100;

        let statusText: 'Pending Entry' | 'Pass' | 'Fail' = 'Pending Entry';
        let statusBadge = 'bg-amber-50 text-amber-700 border-amber-200';

        if (validPcts.length > 0) {
            if (avg >= min && avg <= max) {
                statusText = 'Pass';
                statusBadge = 'bg-emerald-50 text-emerald-700 border-emerald-200';
            } else {
                statusText = 'Fail';
                statusBadge = 'bg-red-50 text-red-700 border-red-200';
            }
        }

        return {
            samples,
            validCount: validPcts.length,
            avg,
            avgStr,
            statusText,
            statusBadge,
            isPass: statusText === 'Pass'
        };
    };

    // Active Machine Metrics
    const activeMetrics = useMemo(() => {
        return getMachineMetrics(activeMachine);
    }, [activeMachine, metadata.standardMin, metadata.standardMax]);

    // All Machines Summary
    const allMachinesMetrics = useMemo(() => {
        return machines.map(m => ({
            ...m,
            metrics: getMachineMetrics(m)
        }));
    }, [machines, metadata.standardMin, metadata.standardMax]);

    // Overall session status
    const overallSessionStatus = useMemo(() => {
        const hasFail = allMachinesMetrics.some(m => m.metrics.statusText === 'Fail');
        const allPending = allMachinesMetrics.every(m => m.metrics.statusText === 'Pending Entry');
        if (allPending) return { text: 'Pending Entry', color: 'bg-amber-100 text-amber-800 border-amber-300' };
        if (hasFail) return { text: 'Fail (Out of Spec)', color: 'bg-red-100 text-red-800 border-red-300' };
        return { text: 'Pass', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' };
    }, [allMachinesMetrics]);

    // ─── 4. Save Single Machine Component ───
    const handleSaveMachineSession = async () => {
        const { validCount, statusText } = activeMetrics;
        if (validCount === 0) {
            alert('Please enter at least one valid sandwich and shell weight sample.');
            return;
        }

        try {
            await pkgOperationsService.saveCreamPercentageData({
                cycleNum: activeMachineIndex + 1,
                formData: {
                    productName: metadata.productName,
                    machineNo: activeMachine.machineNo,
                    line: metadata.line,
                    standardCreamPercentage: metadata.standardCreamPercentage,
                    executiveName: metadata.executiveName || state.sessionData.qaExecutive || ''
                },
                weightData: {
                    sandwichWeights: activeMachine.sandwichWeights,
                    shellWeights: activeMachine.shellWeights
                },
                qualityTourId: state.sessionData.tourId || `QT-${Date.now()}`,
                userName: state.sessionData.qaExecutive || null,
                shiftValue: state.sessionData.shift || 'Shift 1'
            });

            setMachines(prev => prev.map((m, idx) => idx === activeMachineIndex ? { ...m, isSaved: true } : m));
            alert(`Cream percentage recorded for ${activeMachine.machineNo} (Avg: ${activeMetrics.avgStr}% - ${statusText})!`);
        } catch (err) {
            console.error('Error saving machine data:', err);
            alert(`Saved locally for offline synchronization.`);
            setMachines(prev => prev.map((m, idx) => idx === activeMachineIndex ? { ...m, isSaved: true } : m));
        }
    };

    // ─── 5. Sync with Global Packaging Operations Context ───
    useEffect(() => {
        const serializedMachines = allMachinesMetrics.map(item => ({
            machineNo: item.machineNo,
            sandwichWeights: item.sandwichWeights,
            shellWeights: item.shellWeights,
            samplePercentages: item.metrics.samples.map(s => s.result?.pctStr || '0.00'),
            averageCreamPercentage: item.metrics.avgStr,
            status: {
                text: item.metrics.statusText,
                color: item.metrics.statusBadge
            }
        }));

        dispatch({
            type: 'SET_CHECKLIST_DATA',
            payload: {
                metadata: {
                    ...metadata,
                    machineNo: activeMachine.machineNo
                },
                machines: serializedMachines,
                // Backwards-compatible fields for single machine queries
                weightData: {
                    sandwichWeights: activeMachine.sandwichWeights,
                    shellWeights: activeMachine.shellWeights
                },
                averageCreamPercentage: activeMetrics.avgStr,
                status: overallSessionStatus
            }
        });
    }, [metadata, activeMachine, activeMetrics, allMachinesMetrics, overallSessionStatus, dispatch]);

    return (
        <div className="space-y-6" style={{ fontFamily: "'Outfit', sans-serif, system-ui", color: '#1e293b' }}>
            {/* Header & Status Banner */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-wrap justify-between items-center gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <span className="p-2 rounded-lg bg-indigo-50 text-indigo-700 font-bold text-lg">🍰</span>
                        <div>
                            <h3 className="text-xl font-bold text-slate-800 m-0">Cream Percentage Evaluation</h3>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Packaging Operations • Weight of Sandwich & Shells • Machine-Wise Evaluation
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <span className="text-[11px] uppercase tracking-wider text-slate-400 block font-semibold">Session Status</span>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${overallSessionStatus.color}`}>
                            {overallSessionStatus.text === 'Pass' && <span>✓</span>}
                            {overallSessionStatus.text.includes('Fail') && <span>⚠</span>}
                            {overallSessionStatus.text}
                        </span>
                    </div>
                </div>
            </div>

            {/* ─── POINT 1: Variety Selection & Standard Specifications ─── */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2 m-0">
                        <span>📋</span> Variety Standards & Setup Information
                    </h4>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">
                        {metadata.line}
                    </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Variety Dropdown with quick search */}
                    <div className="lg:col-span-2">
                        <div className="flex justify-between items-center mb-1">
                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600">
                                Cream Variety Selection
                            </label>
                            <input
                                type="text"
                                placeholder="🔍 Quick search..."
                                className="border border-slate-300 rounded px-2 py-0.5 text-xs bg-white w-36 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                value={varietySearch}
                                onChange={(e) => setVarietySearch(e.target.value)}
                            />
                        </div>
                        <select
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                            value={metadata.productName}
                            onChange={(e) => handleVarietyChange(e.target.value)}
                        >
                            {availableVarieties.map((v) => (
                                <option key={v.productCode || v.productName} value={v.productName}>
                                    {v.productName} {v.productCode ? `(SKU: ${v.productCode})` : ''} — [Std: {v.standardCreamPercentage.toFixed(2)}%]
                                </option>
                            ))}
                        </select>
                        <span className="text-[11px] text-slate-500 mt-1 block">
                            SKU Code: <strong>{metadata.productCode || 'N/A'}</strong> • {metadata.ruleDescription}
                        </span>
                    </div>

                    {/* Standard Target */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                            Standard Cream % (Target)
                        </label>
                        <div className="relative">
                            <input
                                type="number"
                                step="0.01"
                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-50 font-bold text-slate-800 pr-8 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                value={metadata.standardCreamPercentage}
                                onChange={(e) => setMetadata(prev => ({ ...prev, standardCreamPercentage: e.target.value }))}
                            />
                            <span className="absolute right-3 top-2 text-xs font-bold text-slate-400">%</span>
                        </div>
                        <span className="text-[11px] text-slate-400 mt-1 block">Nominal target for variety</span>
                    </div>

                    {/* Standard Range (Min - Max) */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                            Specification Limits (Min - Max)
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="relative">
                                <input
                                    type="number"
                                    step="0.01"
                                    className="w-full border border-slate-300 rounded-lg px-2.5 py-2 text-sm bg-slate-50 font-bold text-slate-800 pr-6 text-center focus:outline-none"
                                    placeholder="Min"
                                    value={metadata.standardMin}
                                    onChange={(e) => setMetadata(prev => ({ ...prev, standardMin: e.target.value }))}
                                />
                                <span className="absolute right-2 top-2 text-xs text-slate-400">%</span>
                            </div>
                            <div className="relative">
                                <input
                                    type="number"
                                    step="0.01"
                                    className="w-full border border-slate-300 rounded-lg px-2.5 py-2 text-sm bg-slate-50 font-bold text-slate-800 pr-6 text-center focus:outline-none"
                                    placeholder="Max"
                                    value={metadata.standardMax}
                                    onChange={(e) => setMetadata(prev => ({ ...prev, standardMax: e.target.value }))}
                                />
                                <span className="absolute right-2 top-2 text-xs text-slate-400">%</span>
                            </div>
                        </div>
                        <span className="text-[11px] text-slate-400 mt-1 block">Allowable QA specification bounds</span>
                    </div>
                </div>

                {/* Highlighted Specification Badge */}
                <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 flex flex-wrap justify-between items-center gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-base">🎯</span>
                        <div className="text-xs text-indigo-900">
                            <strong>Official Quality Standard for {metadata.productName.slice(0, 35)}...:</strong>{' '}
                            <span>Target: <strong>{metadata.standardCreamPercentage}%</strong> (Tolerance Range: <strong>{metadata.standardMin}%</strong> to <strong>{metadata.standardMax}%</strong>)</span>
                        </div>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-white text-indigo-700 border border-indigo-200">
                        {metadata.ruleDescription}
                    </span>
                </div>
            </div>

            {/* ─── POINT 2: Machine-Wise Selection Tabs & Multi-Machine Navigation ─── */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                <div className="flex flex-wrap justify-between items-center gap-3 border-b border-slate-100 pb-3">
                    <div>
                        <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2 m-0">
                            <span>🏭</span> Machine-Wise Selection & Testing
                        </h4>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Record and evaluate cream percentage machine-wise for each sandwiching machine (up to 10 machines).
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={handleAddMachine}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm transition"
                    >
                        <span>+ ADD Machine ({machines.length}/10)</span>
                    </button>
                </div>

                {/* Machine Tab Pills Bar */}
                <div className="flex flex-wrap gap-2 pt-1">
                    {machines.map((m, idx) => {
                        const mMetrics = getMachineMetrics(m);
                        const isActive = idx === activeMachineIndex;
                        return (
                            <button
                                key={m.id}
                                type="button"
                                onClick={() => setActiveMachineIndex(idx)}
                                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold border transition ${
                                    isActive
                                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                }`}
                            >
                                <span>{m.machineNo}</span>
                                <span className={`text-[10px] px-1.5 py-0.2 rounded font-semibold ${
                                    isActive
                                        ? 'bg-white/20 text-white'
                                        : mMetrics.statusText === 'Pass'
                                        ? 'bg-emerald-100 text-emerald-800'
                                        : mMetrics.statusText === 'Fail'
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-slate-200 text-slate-600'
                                }`}>
                                    {mMetrics.avg > 0 ? `${mMetrics.avgStr}%` : 'Pending'}
                                </span>
                                {machines.length > 1 && (
                                    <span
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveMachine(idx);
                                        }}
                                        className={`ml-1 hover:text-red-300 font-normal ${isActive ? 'text-white' : 'text-slate-400'}`}
                                        title="Remove Machine"
                                    >
                                        ×
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Active Machine Configurator Sub-bar */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-slate-700">Active Machine:</span>
                        <div className="flex flex-wrap gap-1.5">
                            {DEFAULT_MACHINE_OPTIONS.map(opt => (
                                <button
                                    key={opt}
                                    type="button"
                                    onClick={() => handleMachineNoChange(opt)}
                                    className={`px-2.5 py-1 rounded text-xs font-semibold border transition ${
                                        activeMachine.machineNo === opt
                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                                    }`}
                                >
                                    {opt}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Custom Machine:</span>
                        <input
                            type="text"
                            placeholder="e.g. Creamer-4"
                            className="border border-slate-300 rounded px-2 py-1 text-xs bg-white w-28 focus:outline-none"
                            value={customMachineInput}
                            onChange={(e) => setCustomMachineInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && customMachineInput.trim()) {
                                    handleMachineNoChange(customMachineInput.trim());
                                    setCustomMachineInput('');
                                }
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                if (customMachineInput.trim()) {
                                    handleMachineNoChange(customMachineInput.trim());
                                    setCustomMachineInput('');
                                }
                            }}
                            className="px-2 py-1 bg-slate-200 text-slate-700 hover:bg-slate-300 rounded text-xs font-semibold"
                        >
                            Set
                        </button>
                    </div>
                </div>
            </div>

            {/* ─── POINT 3: Separate Options for Weight of Sandwich & Weight of Shells ─── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* 3A: Weight of Sandwich Option */}
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div className="flex items-center gap-2">
                            <span className="p-1.5 bg-emerald-50 text-emerald-700 rounded-md font-bold text-sm">1</span>
                            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800 m-0">
                                Weight of Sandwich (g)
                            </h4>
                        </div>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                            4 Samples
                        </span>
                    </div>

                    <p className="text-xs text-slate-500">
                        Enter gross weight of intact sandwich biscuit packet/sample before separating shells:
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {activeMachine.sandwichWeights.map((w, idx) => (
                            <div key={idx} className="space-y-1">
                                <label className="block text-xs font-bold text-slate-600">
                                    Sample {idx + 1}
                                </label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-mono font-medium pr-7 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                        value={w}
                                        onChange={(e) => handleWeightChange('sandwich', idx, e.target.value)}
                                    />
                                    <span className="absolute right-2.5 top-2 text-xs text-slate-400 font-mono">g</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 3B: Weight of Shells Option */}
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div className="flex items-center gap-2">
                            <span className="p-1.5 bg-blue-50 text-blue-700 rounded-md font-bold text-sm">2</span>
                            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800 m-0">
                                Weight of Shells (g)
                            </h4>
                        </div>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                            4 Samples
                        </span>
                    </div>

                    <p className="text-xs text-slate-500">
                        Enter total weight of the scraped biscuit shells (base & top biscuits):
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {activeMachine.shellWeights.map((w, idx) => {
                            const sandwichVal = parseFloat(activeMachine.sandwichWeights[idx]) || 0;
                            const shellVal = parseFloat(w) || 0;
                            const isExceeded = sandwichVal > 0 && shellVal > sandwichVal;

                            return (
                                <div key={idx} className="space-y-1">
                                    <label className="block text-xs font-bold text-slate-600">
                                        Sample {idx + 1}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            className={`w-full border rounded-lg px-3 py-2 text-sm bg-white font-mono font-medium pr-7 focus:outline-none ${
                                                isExceeded
                                                    ? 'border-red-400 bg-red-50 text-red-800 focus:ring-2 focus:ring-red-500'
                                                    : 'border-slate-300 focus:ring-2 focus:ring-blue-500'
                                            }`}
                                            value={w}
                                            onChange={(e) => handleWeightChange('shell', idx, e.target.value)}
                                        />
                                        <span className="absolute right-2.5 top-2 text-xs text-slate-400 font-mono">g</span>
                                    </div>
                                    {isExceeded && (
                                        <span className="text-[10px] text-red-600 font-semibold block leading-tight">
                                            Shell wt &gt; sandwich!
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ─── POINT 4: Automatic Cream % Calculation Table & Live Evaluation ─── */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                <div className="flex flex-wrap justify-between items-center gap-3 border-b border-slate-100 pb-3">
                    <div>
                        <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800 flex items-center gap-2 m-0">
                            <span>⚖</span> Automatic Cream % Calculation Table ({activeMachine.machineNo})
                        </h4>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Formula: <strong>Cream % = ((Weight of Sandwich − Weight of Shells) / Weight of Sandwich) × 100</strong>
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold border ${activeMetrics.statusBadge}`}>
                            Machine Status: {activeMetrics.statusText}
                        </span>
                    </div>
                </div>

                {/* Calculation Table */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-left text-sm border-collapse">
                        <thead>
                            <tr className="bg-slate-100 text-slate-700 text-xs font-bold border-b border-slate-200">
                                <th className="p-3 border-r border-slate-200">Sample No.</th>
                                <th className="p-3 border-r border-slate-200">Wt. of Sandwich (g)</th>
                                <th className="p-3 border-r border-slate-200">Wt. of Shell (g)</th>
                                <th className="p-3 border-r border-slate-200">Weight of Cream (g)</th>
                                <th className="p-3 border-r border-slate-200">Actual Cream %</th>
                                <th className="p-3 border-r border-slate-200">Spec Range</th>
                                <th className="p-3 text-center">Status</th>
                                <th className="p-3 text-center bg-slate-50 border-l border-slate-200">Overall Machine AVG</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 bg-white">
                            {activeMetrics.samples.map((s, index) => {
                                const swNum = parseFloat(s.sandwich) || 0;
                                const shNum = parseFloat(s.shell) || 0;
                                const creamWeight = swNum > 0 && shNum > 0 && swNum >= shNum ? (swNum - shNum).toFixed(2) : '-';
                                const pctStr = s.result?.pctStr;
                                const min = parseFloat(metadata.standardMin) || 0;
                                const max = parseFloat(metadata.standardMax) || 100;
                                const sampleNum = s.result?.pct || 0;

                                const isSampleOk = s.result && !s.result.error && sampleNum >= min && sampleNum <= max;
                                const hasData = s.result && !s.result.error;

                                return (
                                    <tr key={index} className="hover:bg-slate-50">
                                        <td className="p-3 font-semibold text-slate-700 border-r border-slate-200">
                                            Sample {s.index}
                                        </td>
                                        <td className="p-3 font-mono text-slate-800 border-r border-slate-200">
                                            {swNum > 0 ? `${swNum.toFixed(2)} g` : '-'}
                                        </td>
                                        <td className="p-3 font-mono text-slate-800 border-r border-slate-200">
                                            {shNum > 0 ? `${shNum.toFixed(2)} g` : '-'}
                                        </td>
                                        <td className="p-3 font-mono text-indigo-700 font-semibold border-r border-slate-200">
                                            {creamWeight !== '-' ? `${creamWeight} g` : '-'}
                                        </td>
                                        <td className="p-3 font-mono font-bold text-base text-indigo-600 border-r border-slate-200">
                                            {pctStr ? `${pctStr}%` : '-'}
                                        </td>
                                        <td className="p-3 text-xs text-slate-500 border-r border-slate-200">
                                            {metadata.standardMin}% – {metadata.standardMax}%
                                        </td>
                                        <td className="p-3 text-center border-r border-slate-200">
                                            {hasData ? (
                                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded border ${
                                                    isSampleOk
                                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                        : 'bg-red-50 text-red-700 border-red-200'
                                                }`}>
                                                    {isSampleOk ? '✓ In-Spec' : '⚠ Out of Spec'}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-slate-400">-</span>
                                            )}
                                        </td>
                                        {index === 0 && (
                                            <td rowSpan={4} className="p-4 text-center align-middle bg-slate-50 border-l border-slate-200">
                                                <div className="flex flex-col items-center justify-center space-y-1">
                                                    <span className="text-xs uppercase font-bold text-slate-400 tracking-wider">
                                                        Machine Average
                                                    </span>
                                                    <span className="text-3xl font-extrabold font-mono text-slate-800">
                                                        {activeMetrics.avgStr}%
                                                    </span>
                                                    <span className="text-xs text-slate-500 font-medium">
                                                        Target: <strong>{metadata.standardCreamPercentage}%</strong>
                                                    </span>
                                                    <span className={`mt-2 px-3 py-1 rounded-full text-xs font-bold border ${activeMetrics.statusBadge}`}>
                                                        {activeMetrics.statusText}
                                                    </span>
                                                    <span className="text-[11px] text-slate-400 mt-1">
                                                        {activeMetrics.validCount}/4 samples calculated
                                                    </span>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Save Machine Component Action */}
                <div className="pt-3 border-t border-slate-100 flex flex-wrap justify-between items-center gap-3">
                    <div className="text-xs text-slate-500">
                        All machines will be automatically bundled and synchronized into Dataverse entity <code>cr3ea_creams</code> upon session submission.
                    </div>
                    <button
                        type="button"
                        onClick={handleSaveMachineSession}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm transition"
                    >
                        <span>✓ Save {activeMachine.machineNo} Session Data</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreamPercentage;
